/* eslint-disable eqeqeq */
import { initialProductsQuantity, showMoreProducts } from 'utils/constants';
import * as CatalogTypes from './catalog.types';
import * as catalogAPI from 'api/endpoints/catalog/catalog.api';
import { catalogError } from 'utils/errorCodes';
import { notificationErr } from 'views/components/UI/notification';
import {
  IBodyProductSearch,
  ICatalogState,
  TProductInfoFieldNameTypes,
  TQuerySearchArray,
  TSortBy,
} from 'interfaces/catalog.interface';
import {
  COLOR_FILTER,
  DIVISION_FILTER,
  FAMILY_FILTER,
  GENDER_FILTER,
  SEASON_FILTER,
  SEGMENTATION_FILTER,
} from 'views/pages/main/catalog/catalogPage/components/catalogFilters/drawerCatalogFilters/filtersConstants';
import { getProductAttributesAPI } from 'api/endpoints/general/general.api';
import { messageError, messageSuccess } from 'views/components/UI/message';
import {
  IFavorite,
  IgetCatalogResponseData,
  IProductState,
} from 'api/endpoints/catalog/catalog.response.interface';
import { ITranslate } from 'interfaces/general.interface';
import { TcatalogDispatchReducer } from './catalog.reducer';
import { sizeOrder } from '../utils/sizeOrder';
import { updateCatalogReferencesAPI } from 'api/endpoints/catalogUpdateReferences/catalogUpdateReferences.api';
import { IupdateCatalogReferencesAPIBody } from 'api/endpoints/catalogUpdateReferences/catalogUpdateReferences.response.interface';
import { IsetSelectedValuesProps } from 'interfaces/catalogUpdateReferences.interface';

// Catalog
export async function getCatalogAction({
  dispatch,
  brandId,
  bodyProductSearch,
  signal,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  brandId?: string;
  bodyProductSearch: IBodyProductSearch;
  signal: AbortSignal | undefined;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: true });
  try {
    const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
      JSON.stringify(bodyProductSearch)
    );
    copyOfBodyProductSearch.options.index = 0;
    if (brandId) {
      copyOfBodyProductSearch.options.brand_id = [brandId];
    }
    const products = await getAndOrderImagesCatalog({
      dispatch,
      body: copyOfBodyProductSearch,
      signal,
      token,
      isRelatedProducts: false,
    });
    if (products) {
      dispatch({
        type: CatalogTypes.SET_BODY_PRODUCT_SEARCH,
        payload: copyOfBodyProductSearch,
      });
      dispatch({
        type: CatalogTypes.SET_BODY_PRODUCT_SEARCH_TEMP,
        payload: copyOfBodyProductSearch,
      });
      dispatch({
        type: CatalogTypes.GET_CATALOG,
        payload: products,
      });
      return true;
    }
    return false;
  } catch (err) {
    notificationErr(catalogError.get_catalog_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: false });
  }
}

export async function addMoreProductsToCatalogAction({
  dispatch,
  bodyProductSearch,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearch: IBodyProductSearch;
  signal: AbortSignal | undefined;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS, payload: true });
  const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
    JSON.stringify(bodyProductSearch)
  );
  if (copyOfBodyProductSearch.options.index === 0) {
    copyOfBodyProductSearch.options.index = initialProductsQuantity;
  }

  try {
    const products = await getAndOrderImagesCatalog({
      dispatch,
      body: copyOfBodyProductSearch,
      signal,
      token,
      isRelatedProducts: false,
    });
    if (!products) return false;
    dispatch({
      type: CatalogTypes.INCREMENT_PAGINATION_INDEX,
      payload: copyOfBodyProductSearch.options.index + products.data.length,
    });
    dispatch({
      type: CatalogTypes.ADD_MORE_TO_CATALOG,
      payload: products,
    });
    return true;
  } catch (err) {
    // return notificationErr(catalogError.add_more_products_to_catalog_001, translate)
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_MORE_PRODUCTS, payload: false });
  }
}

export async function addLikeAction({
  dispatch,
  userId,
  productId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  userId: string;
  productId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({
    type: CatalogTypes.SET_IS_LOADING_ADD_LIKE,
    payload: {
      productId,
      isLoading: true,
    },
  });
  try {
    const likeFetch = await catalogAPI.addLikeAPI(userId, productId, token);
    if (likeFetch.response.status === 200) {
      const objectToPush: IFavorite = {
        favorite_id: likeFetch.data.id,
        user_id: userId,
      };
      dispatch({
        type: CatalogTypes.ADD_LIKE,
        payload: {
          productId,
          favorite: objectToPush,
        },
      });
      return true;
    }
    notificationErr(catalogError.addLike_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogError.addLike_001, translate);
    return false;
  } finally {
    dispatch({
      type: CatalogTypes.SET_IS_LOADING_ADD_LIKE,
      payload: {
        productId: '',
        isLoading: false,
      },
    });
  }
}

export async function removeLikeAction({
  dispatch,
  productId,
  favorites,
  userId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  productId: string;
  favorites: IFavorite[];
  userId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({
    type: CatalogTypes.SET_IS_LOADING_REMOVE_LIKE,
    payload: {
      productId,
      isLoading: true,
    },
  });
  const findFavorite = favorites.find((favorite) => favorite.user_id === userId);
  if (!findFavorite) {
    return false;
  }
  try {
    const likeFetch = await catalogAPI.removeLikeAPI(findFavorite.favorite_id, token);
    if (likeFetch.response.status === 200) {
      dispatch({
        type: CatalogTypes.REMOVE_LIKE,
        payload: {
          productId,
          favorite_id: findFavorite.favorite_id,
        },
      });
      return true;
    }
    notificationErr(catalogError.removeLike_002, translate);
    return false;
  } catch (err) {
    notificationErr(catalogError.removeLike_001, translate);
    return false;
  } finally {
    dispatch({
      type: CatalogTypes.SET_IS_LOADING_REMOVE_LIKE,
      payload: {
        productId: '',
        isLoading: false,
      },
    });
  }
}

// Filters
export async function applyFiltersAction({
  dispatch,
  catalogState,
  translate,
  signal,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  catalogState: ICatalogState;
  translate: ITranslate;
  signal: AbortSignal | undefined;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: true });
  dispatch({ type: CatalogTypes.INCREMENT_PAGINATION_INDEX, payload: 0 });
  const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
  copyOfCatalogState.bodyProductSearch.options.index = 0;
  copyOfCatalogState.bodyProductSearch = copyOfCatalogState.bodyProductSearchTemp;
  try {
    const getCatalog = await getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
    if (getCatalog) {
      return true;
    }
    return false;
  } catch (err) {
    notificationErr(catalogError.filter_catalog_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: false });
  }
}

export async function addBodyFiltersAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  field: string,
  value: string
) {
  return dispatch({
    type: CatalogTypes.ADD_BODY_FILTERS,
    payload: {
      field,
      value,
    },
  });
}

export async function removeBodyFiltersAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  field: string,
  value: string
) {
  return dispatch({
    type: CatalogTypes.REMOVE_BODY_FILTERS,
    payload: {
      field,
      value,
    },
  });
}

export async function resetBodyFiltersAction({
  dispatch,
  bodyProductSearch,
  setOpenDrawer,
  signal,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearch: IBodyProductSearch;
  setOpenDrawer: any;
  signal: AbortSignal | undefined;
  translate: ITranslate;
  token: string;
}) {
  setOpenDrawer && setOpenDrawer(false);
  const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
    JSON.stringify(bodyProductSearch)
  );
  copyOfBodyProductSearch.filter.color = [];
  copyOfBodyProductSearch.filter.season = [];
  copyOfBodyProductSearch.filter.segmentation = [];
  copyOfBodyProductSearch.filter.division = [];
  copyOfBodyProductSearch.filter.family = [];
  copyOfBodyProductSearch.filter.gender = [];
  copyOfBodyProductSearch.filter.pvi = [];
  copyOfBodyProductSearch.filter.pvpr = [];
  copyOfBodyProductSearch.filter.tags = [];
  copyOfBodyProductSearch.filter.tiers = [];
  copyOfBodyProductSearch.filter.reference = [];
  copyOfBodyProductSearch.options.favorite = '';
  copyOfBodyProductSearch.options.order = [];
  copyOfBodyProductSearch.options.limit = initialProductsQuantity;
  copyOfBodyProductSearch.options.index = 0;
  copyOfBodyProductSearch.options.with_images = null;
  copyOfBodyProductSearch.options.search = [];
  copyOfBodyProductSearch.options.order = [];
  copyOfBodyProductSearch.options.group = [];
  getCatalogAction({
    dispatch,
    bodyProductSearch: copyOfBodyProductSearch,
    signal,
    translate,
    token,
  });
  dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
  dispatch({ type: CatalogTypes.SET_IS_EMPTY_PRODUCTS, payload: false });
  return dispatch({ type: CatalogTypes.RESET_BODY_FILTERS });
}

export async function setPriceSlidersAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  field: string,
  value: string
) {
  return dispatch({
    type: CatalogTypes.SET_PRICE_SLIDERS,
    payload: {
      field,
      value,
    },
  });
}

export async function setFavoritesAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  userId: string | null
) {
  dispatch({ type: CatalogTypes.INCREMENT_PAGINATION_INDEX, payload: 0 });
  return dispatch({ type: CatalogTypes.SET_FAVORITES, payload: userId });
}

export async function resetPriceSliderAction({
  dispatch,
  type,
  bodyProductSearch,
  signal,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  type: string;
  bodyProductSearch: IBodyProductSearch;
  signal: AbortSignal | undefined;
  translate: ITranslate;
  token: string;
}) {
  dispatch({ type: CatalogTypes.RESET_PRICE_SLIDER, payload: { type: type } });
  const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
    JSON.stringify(bodyProductSearch)
  );
  copyOfBodyProductSearch.filter[type as keyof typeof copyOfBodyProductSearch.filter] =
    [];
  getCatalogAction({
    dispatch,
    bodyProductSearch: copyOfBodyProductSearch,
    signal,
    translate,
    token,
  });
}

// Product
export async function getProductAction({
  dispatch,
  productId,
  brandId,
  languageId,
  userId,
  translate,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  productId: string;
  brandId: string;
  languageId: string;
  userId: string;
  translate: ITranslate;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING_PRODUCT, payload: true });
  try {
    const getProductFetch = await catalogAPI.getProductAPI(
      productId,
      brandId,
      languageId,
      userId,
      token
    );

    if (getProductFetch.response.status !== 200) {
      notificationErr(catalogError.getProduct_002, translate);
      return false;
    }

    const {
      product_id,
      product_description,
      ranking,
      brand_id,
      brand_name,
      brand_logo,
      color,
      color_id,
      color_code,
      reference_id,
      reference_code,
      material,
      tags,
      images,
      ean_size,
      favorite,
      season,
      segmentation,
      family,
      division,
      gender,
      sizes,
      prices,
      other_colors,
    } = getProductFetch.data;

    // Sort by las two digits before the image url extension
    const sortedUrls = images?.sort((a, b) => {
      const matchA = a.url.match(/(\d{2})\.\w+$/);
      const matchB = b.url.match(/(\d{2})\.\w+$/);

      if (!matchA) return 1;
      if (!matchB) return -1;

      const numA = parseInt(matchA[1], 10);
      const numB = parseInt(matchB[1], 10);

      // Priorizar la imagen que termina en 1
      if (numA % 10 === 1 && numB % 10 !== 1) return -1;
      if (numB % 10 === 1 && numA % 10 !== 1) return 1;

      // Si ambos terminan en 1, ordenar de menor a mayor
      if (numA % 10 === 1 && numB % 10 === 1) return numA - numB;

      return numA - numB;
    });

    const productState: IProductState = {
      product_id: product_id,
      product_description: product_description,
      ranking: ranking,
      brand_id: brand_id,
      brand_name: brand_name,
      brand_logo: brand_logo,
      color_id: color_id,
      color_code: color_code,
      reference_id: reference_id,
      reference_code: reference_code,
      material: material,
      tags: tags,
      images: sortedUrls ?? [],
      ean_size: ean_size.sort(
        (a, b) =>
          ((sizeOrder as any)[a.size] || Infinity) -
          ((sizeOrder as any)[b.size] || Infinity)
      ),
      favorite: favorite,
      season: season,
      segmentation: segmentation,
      family: family,
      division: division,
      gender: gender,
      color: color,
      sizes: sizes.sort(
        (a, b) =>
          ((sizeOrder as any)[a] || Infinity) - ((sizeOrder as any)[b] || Infinity)
      ),
      prices: prices.sort(
        (a, b) =>
          ((sizeOrder as any)[a.size] || Infinity) -
          ((sizeOrder as any)[b.size] || Infinity)
      ),
      other_colors: other_colors,
    };
    dispatch({
      type: CatalogTypes.GET_PRODUCT,
      payload: productState,
    });
    return true;
  } catch (err) {
    notificationErr(catalogError.getProduct_001, translate);
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_PRODUCT, payload: false });
  }
}

export async function updateProductAction({
  dispatch,
  fieldName,
  value,
  selectedValues,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  fieldName: TProductInfoFieldNameTypes;
  value: string;
  selectedValues: Array<IsetSelectedValuesProps>;
  token: string;
}) {
  const updateProductBody: IupdateCatalogReferencesAPIBody = {
    update: {
      [fieldName]: value,
    },
    filter: selectedValues,
  };
  try {
    const updateProductFetch = await updateCatalogReferencesAPI(updateProductBody, token);
    if (updateProductFetch.response.status !== 200) {
      messageError('Error al actualizar el producto');
      return false;
    } else {
      dispatch({
        type: CatalogTypes.UPDATE_PRODUCT,
        payload: { fieldName, value },
      });
      messageSuccess(`Producto actualizado correctamente`);
      return true;
    }
  } catch (error) {
    console.log(error);
    messageError('Error del servidor al actualizar el producto');
    return false;
  }
}

// Related Products
export async function getRelatedProductsAction({
  dispatch,
  bodyProductSearch,
  family,
  token,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  bodyProductSearch: IBodyProductSearch;
  family: string;
  token: string;
}): Promise<boolean> {
  dispatch({ type: CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS, payload: true });
  try {
    const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
      JSON.stringify(bodyProductSearch)
    );
    let body: IBodyProductSearch = {
      options: {
        brand_id: copyOfBodyProductSearch.options.brand_id,
        favorite: null,
        with_images: true,
        profile: false,
        search: [],
        language_id: copyOfBodyProductSearch.options.language_id,
        order: [],
        group: [],
        limit: initialProductsQuantity,
        index: 0,
      },
      filter: {
        reference: [],
        color: [],
        season: [],
        segmentation: [],
        division: [],
        family: [family],
        gender: [],
        pvi: [],
        pvpr: [],
        tags: [],
        tiers: [],
      },
    };
    const products = await getAndOrderImagesCatalog({
      dispatch,
      body,
      signal: undefined,
      token,
      isRelatedProducts: true,
    });

    if (products && products.data.length > 0) {
      dispatch({
        type: CatalogTypes.SET_RELATED_PRODUCTS,
        payload: {
          data: products.data,
          count: products.data.length,
          active_seasons: 0,
          published_catalogs: 0,
          total: 0,
          of: 0,
        },
      });
      return true;
    } else {
      body.options.with_images = null;
      body.filter.family = [];
      body.options.limit = 30;
      body.options.index = 0;

      const products = await getAndOrderImagesCatalog({
        dispatch,
        body,
        signal: undefined,
        token,
        isRelatedProducts: true,
      });

      if (products) {
        dispatch({
          type: CatalogTypes.SET_RELATED_PRODUCTS,
          payload: {
            data: products.data,
            count: products.data.length,
            active_seasons: 0,
            published_catalogs: 0,
            total: 0,
            of: 0,
          },
        });
        return true;
      }
    }
    return false;
    // copyOfBodyProductSearch.options.index = copyOfBodyProductSearch.options.index - 10;
    // if (Math.sign(copyOfBodyProductSearch.options.index) === -1) {
    //   copyOfBodyProductSearch.options.index = 0;
    // }
    // const products = await getAndOrderImagesCatalog({
    //   dispatch,
    //   body: copyOfBodyProductSearch,
    //   token,
    //   isRelatedProducts: true,
    // });
    // if (products) {
    //   if (products.data.length < 20) {
    //     body.options.index = 0;
    //     const moreProducts = await getAndOrderImagesCatalog({
    //       dispatch,
    //       body,
    //       token,
    //       isRelatedProducts: true,
    //     });
    //     const allProducts = moreProducts
    //       ? products.data.concat(moreProducts.data)
    //       : products.data;
    //     return dispatch({
    //       type: CatalogTypes.SET_RELATED_PRODUCTS,
    //       payload: {
    //         data: allProducts.slice(0, 30),
    //         count: allProducts.slice(0, 30).length,
    //         active_seasons: 0,
    //         published_catalogs: 0,
    //         total: 0,
    //         of: 0,
    //       },
    //     });
    //   }
    //   return dispatch({
    //     type: CatalogTypes.SET_RELATED_PRODUCTS,
    //     payload: {
    //       data: products.data.slice(0, 30),
    //       count: products.data.slice(0, 30).length,
    //       active_seasons: 0,
    //       published_catalogs: 0,
    //       total: 0,
    //       of: 0,
    //     },
    //   });
    // }
  } catch (err) {
    // return notificationErr(catalogError.get_catalog_001, translate)
    return false;
  } finally {
    dispatch({ type: CatalogTypes.SET_IS_LOADING_RELATED_PRODUCTS, payload: false });
  }
}

// Search Engine
export async function setQuerySearchAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  query: string
) {
  return dispatch({ type: CatalogTypes.SET_QUERY_SEARCH, payload: query });
}

export async function addToBodySearchAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  query: string,
  catalogState: ICatalogState,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  try {
    const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    copyOfCatalogState.bodyProductSearch.options.index = 0;
    copyOfCatalogState.bodyProductSearch.options.search.push(`%${query}%`);
    getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (err) {
    console.log(err);
  }
}

export async function removeFromBodySearchAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  query: string,
  catalogState: ICatalogState,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  try {
    const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    copyOfCatalogState.bodyProductSearch.options.index = 0;
    const filteredSearch = copyOfCatalogState.bodyProductSearch.options.search.filter(
      (item) => item !== query
    );
    copyOfCatalogState.bodyProductSearch.options.search = filteredSearch;
    copyOfCatalogState.bodyProductSearchTemp = copyOfCatalogState.bodyProductSearch;
    getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (err) {
    console.log(err);
  }
}

export async function addToQuerySearchArrayAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  value: TQuerySearchArray
) {
  return dispatch({ type: CatalogTypes.ADD_TO_QUERY_SEARCH_ARRAY, payload: value });
}

export async function removeFromQuerySearchArrayAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  value: TQuerySearchArray
) {
  return dispatch({ type: CatalogTypes.REMOVE_FROM_QUERY_SEARCH_ARRAY, payload: value });
}

export async function removeAllFromBodySearchAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  catalogState: ICatalogState,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  try {
    const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    copyOfCatalogState.bodyProductSearch.options.limit = initialProductsQuantity;
    copyOfCatalogState.bodyProductSearch.options.index = 0;
    copyOfCatalogState.bodyProductSearch.options.search = [];
    copyOfCatalogState.bodyProductSearch.options.favorite = '';
    copyOfCatalogState.bodyProductSearch.filter = {
      reference: [],
      color: [],
      season: [],
      segmentation: [],
      division: [],
      family: [],
      gender: [],
      pvi: [],
      pvpr: [],
      tags: [],
      tiers: [],
    };
    copyOfCatalogState.bodyProductSearchTemp = copyOfCatalogState.bodyProductSearch;
    copyOfCatalogState.querysearchArray = [];
    getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (err) {
    console.log(err);
  }
}

export async function removeFromBodySearchFilterAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  value: TQuerySearchArray,
  catalogState: ICatalogState,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  const { field } = value;
  try {
    const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
    copyOfCatalogState.bodyProductSearch.options.index = 0;
    const filteredQuerySearchArray = copyOfCatalogState.querysearchArray.filter(
      (item) => item.value[0] !== value?.value[0]
    );
    const filteredArray = (
      copyOfCatalogState.bodyProductSearch.filter[
        field as keyof typeof copyOfCatalogState.bodyProductSearch.filter
      ] as string[]
    ).filter((item) => !value.value.includes(item as string));
    copyOfCatalogState.bodyProductSearch.filter[
      field as keyof typeof copyOfCatalogState.bodyProductSearch.filter
    ] = filteredArray as any;
    dispatch({
      type: CatalogTypes.SET_BODY_SEARCH_FILTER,
      payload: {
        querySearchArray: filteredQuerySearchArray,
      },
    });
    await getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (err) {
    console.log(err);
  }
}

// Sort By
export async function setSortByAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  sortBy: TSortBy,
  catalogState: ICatalogState,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  const copyOfCatalogState: ICatalogState = JSON.parse(JSON.stringify(catalogState));
  copyOfCatalogState.bodyProductSearch.options.order = [sortBy];
  copyOfCatalogState.bodyProductSearch.options.index = 0;
  dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: true });
  dispatch({ type: CatalogTypes.SET_SORT_BY, payload: sortBy });
  try {
    await getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfCatalogState.bodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (error) {
    console.log('error');
  } finally {
    return dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: false });
  }
}

export async function setSortByHaveImagesAction(
  dispatch: React.Dispatch<TcatalogDispatchReducer>,
  haveImages: boolean | null,
  bodyProductSearch: IBodyProductSearch,
  signal: AbortSignal | undefined,
  translate: ITranslate,
  token: string
) {
  dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: true });
  try {
    const copyOfBodyProductSearch: IBodyProductSearch = JSON.parse(
      JSON.stringify(bodyProductSearch)
    );
    copyOfBodyProductSearch.options.with_images = haveImages;
    await getCatalogAction({
      dispatch,
      bodyProductSearch: copyOfBodyProductSearch,
      signal,
      translate,
      token,
    });
  } catch (error) {
    console.log(error);
  } finally {
    return dispatch({ type: CatalogTypes.SET_IS_LOADING, payload: false });
  }
}

type TgetProductAttributesActionProps = {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  brandId: string;
  token: string;
};
export async function getProductAttributesAction({
  dispatch,
  brandId,
  token,
}: TgetProductAttributesActionProps) {
  const [seasons, divisions, genders, families, colors, segmentations] =
    await Promise.all([
      getProductAttributesAPI({ brandId, attribute: SEASON_FILTER, token }),
      getProductAttributesAPI({ brandId, attribute: DIVISION_FILTER, token }),
      getProductAttributesAPI({ brandId, attribute: GENDER_FILTER, token }),
      getProductAttributesAPI({ brandId, attribute: FAMILY_FILTER, token }),
      getProductAttributesAPI({ brandId, attribute: COLOR_FILTER, token }),
      getProductAttributesAPI({ brandId, attribute: SEGMENTATION_FILTER, token }),
    ]);

  const haveSeasons = seasons.response.status === 200 && seasons.data.length > 0;
  const haveDivisions = divisions.response.status === 200 && divisions.data.length > 0;
  const haveGenders = genders.response.status === 200 && genders.data.length > 0;
  const haveFamilies = families.response.status === 200 && families.data.length > 0;
  const haveColors = colors.response.status === 200 && colors.data.length > 0;
  const haveSegmentations =
    segmentations.response.status === 200 && segmentations.data.length > 0;

  dispatch({
    type: CatalogTypes.SET_PRODUCT_ATTRIBUTES,
    payload: {
      season: haveSeasons ? seasons.data : [],
      division: haveDivisions ? divisions.data : [],
      gender: haveGenders ? genders.data : [],
      family: haveFamilies ? families.data : [],
      color: haveColors ? colors.data : [],
      segmentation: haveSegmentations ? segmentations.data : [],
    },
  });
}

///////////////////////////////////
///////////////////////////////////
async function getAndOrderImagesCatalog({
  dispatch,
  body,
  signal,
  token,
  isRelatedProducts,
}: {
  dispatch: React.Dispatch<TcatalogDispatchReducer>;
  body: IBodyProductSearch;
  signal?: AbortSignal | undefined;
  token: string;
  isRelatedProducts: boolean;
}): Promise<IgetCatalogResponseData | null> {
  try {
    const catalogFetch = await catalogAPI.getCatalogAPI({ body, token, signal });
    dispatch({ type: CatalogTypes.SET_IS_EMPTY_PRODUCTS, payload: false });
    if (catalogFetch.response.status === 404 || catalogFetch.data.data.length === 0) {
      dispatch({ type: CatalogTypes.SET_IS_EMPTY_PRODUCTS, payload: true });
    }
    if (catalogFetch.data.data?.length < showMoreProducts) {
      dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: false });
    } else {
      dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
    }
    const products = catalogFetch.data.data;

    type IImageWithNumber = {
      original: string;
      number: number;
    };

    await Promise.all(
      products.map(async (item) => {
        if (item?.images && item.images.length > 0) {
          let imagesWithNumbers: IImageWithNumber[] = [];
          let imagesWithoutNumbers: string[] = [];
          item.images.forEach((image) => {
            const number = parseInt(getExtension(image).split('.')[0], 10);
            if (!isNaN(number)) {
              imagesWithNumbers.push({ original: image, number });
            } else {
              imagesWithoutNumbers.push(image);
            }
          });
          imagesWithNumbers.sort((a, b) => a.number - b.number);
          const indexEndsWithOne = imagesWithNumbers.findIndex(
            (image) => image.number % 10 === 1
          );
          if (indexEndsWithOne > -1) {
            const [imageEndsWithOne] = imagesWithNumbers.splice(indexEndsWithOne, 1);
            imagesWithNumbers.unshift(imageEndsWithOne);
          }
          const uniqueNumericImages = Array.from(
            new Set(imagesWithNumbers.map((image) => image.original))
          );
          item.images = uniqueNumericImages.concat(imagesWithoutNumbers);
        }
      })
    );

    if (isRelatedProducts === false) {
      if (
        catalogFetch.data.data?.length === 0 ||
        catalogFetch.data.data?.length < showMoreProducts
      ) {
        dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: false });
      } else {
        dispatch({ type: CatalogTypes.SET_THERE_ARE_MORE_PRODUCTS, payload: true });
      }
    }

    const catalogObject: IgetCatalogResponseData = {
      ...catalogFetch.data,
      data: products ?? [],
    };

    return catalogObject;
  } catch (error) {
    return null;
    // console.log(error);
  }
}

function getExtension(url: string) {
  return (url = url.substr(1 + url.lastIndexOf('/')).split('?')[0])
    .split('#')[0]
    .substr(url.lastIndexOf('.') - 2);
}
